import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import icpCampaignService, { IcpCampaign } from '../services/icpCampaignService';
import { PageHeader, useConfirm } from '../components/ui';
import { routeById } from '../navigation/routes';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { RechteHinweis } from '../vertrieb/RechteHinweis';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  searched: 'Gesucht',
  enriching: 'Anreicherung läuft',
  enriched: 'Angereichert',
  imported: 'Importiert',
  ready: 'Bereit',
  active: 'Aktiv',
};

const IcpCampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  // Welle 2: Anlegen und Loeschen sind Schreibaufrufe (vertrieb.bearbeiten).
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const [campaigns, setCampaigns] = useState<IcpCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCampaigns(await icpCampaignService.list());
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setError(null);
    try {
      const c = await icpCampaignService.create({ name: newName.trim() });
      setNewName('');
      navigate(`/icp-campaigns/${c.id}`);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  const handleDelete = async (campaign: IcpCampaign) => {
    const ok = await confirm({
      title: `Kampagne „${campaign.name}“ löschen?`,
      message: `Die Kampagne wird mit ihren ${campaign._count?.prospects ?? 0} Prospects dauerhaft gelöscht. Rückgängig nur durch Neuanlage.`,
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await icpCampaignService.remove(campaign.id);
      setCampaigns((prev) => prev.filter((c) => c.id !== campaign.id));
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  return (
    <div className="page-container-wide">
      <PageHeader title="Leads nach Zielkundenprofil (ICP)" subtitle={routeById('icp-campaigns')?.description} />
      <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-4)' }}>
        Aus einem ICP passende Leads aus Lemlist holen, anreichern und als Kampagne aufbauen.
      </p>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--space-4)' }}>
          {error}
        </div>
      )}

      <RechteHinweis was="ICP-Kampagnen anlegen und bearbeiten" />

      {darfBearbeiten && (
        <div className="card" style={{ marginBottom: 'var(--space-5)' }}>
          <h3 className="card-title">Neue ICP-Kampagne</h3>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ flex: 1, margin: 0 }}>
              <label className="form-label">Name</label>
              <input
                className="form-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="z. B. DACH SaaS VP Sales Q3"
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
            <button className="btn btn-primary" onClick={handleCreate} disabled={!newName.trim()}>
              Anlegen
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="card-title">Meine Kampagnen</h3>
        {loading ? (
          <p className="text-muted">Lade…</p>
        ) : campaigns.length === 0 ? (
          <p className="text-muted text-sm">Noch keine Kampagne.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Prospects</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id}>
                  <td>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => navigate(`/icp-campaigns/${c.id}`)}
                    >
                      {c.name}
                    </button>
                  </td>
                  <td>
                    <span className="badge">{STATUS_LABELS[c.status] || c.status}</span>
                  </td>
                  <td>{c._count?.prospects ?? 0}</td>
                  <td style={{ textAlign: 'right' }}>
                    {darfBearbeiten && (
                      <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(c)}>
                        Löschen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default IcpCampaignsPage;
